'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { useIsomorphicLayoutEffect } from '@/lib/hooks/useIsomorphicLayoutEffect';
import type { Project } from '@/lib/data';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { portfolioCopy } from '@/lib/i18n/pages/portfolio';
import { isRtl } from '@/lib/i18n/config';
import { getPublicProjects } from '@/lib/services/publicProjectService';

interface PortfolioGridProps {
  projects?: Project[];
  activeCategory?: string;
  onCategoryChange?: (category: string) => void;
}

export function PortfolioGrid({ projects: fallbackProjects = [] }: PortfolioGridProps) {
  const { locale, href } = useLocale();
  const copy = pageCopy(portfolioCopy, locale);
  const t = copy.grid;
  const rtl = isRtl(locale);
  const [projects, setProjects] = useState<Project[]>(fallbackProjects);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [errorImages, setErrorImages] = useState<Set<string>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  const handleImageLoad = useCallback((projectId: string) => {
    setLoadedImages(prev => new Set(prev).add(projectId));
  }, []);

  const handleImageError = useCallback((projectId: string) => {
    // Try fallback local image before showing error
    const fb = fallbackProjects.find(f => String(f.id) === projectId || f.slug === projectId);
    if (fb && fb.image) {
      setProjects(prev => prev.map(p => String(p.id) === projectId ? { ...p, image: fb.image } : p));
      return; // Don't mark as error — let it retry with fallback image
    }
    setErrorImages(prev => new Set(prev).add(projectId));
    setLoadedImages(prev => new Set(prev).add(projectId));
  }, [fallbackProjects]);

  useEffect(() => {
    // Use local fallback data directly — Firestore image URLs are unreliable
    if (fallbackProjects.length > 0) {
      setProjects(fallbackProjects);
      setLoading(false);
      return;
    }

    // Only fetch from Firestore if no fallback data
    const fetchProjects = async () => {
      try {
        const firestoreProjects = await getPublicProjects();
        if (firestoreProjects.length > 0) {
          setProjects(firestoreProjects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [fallbackProjects]);

  // Animate items on load + add vertical parallax
  useIsomorphicLayoutEffect(() => {
    if (loading || !gridRef.current || typeof window === 'undefined') return;

    const ctx = gsap.context(() => {
      const items = gridRef.current?.querySelectorAll('.showcase-item');
      if (items) {
        gsap.fromTo(
          items,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
          }
        );
      }

      // Animate captions to visible
      const captions = gridRef.current?.querySelectorAll('.slide-caption');
      if (captions) {
        gsap.to(captions, {
          opacity: 1,
          duration: 0.6,
          stagger: 0.15,
          delay: 0.3,
          ease: 'power2.out',
        });
      }

      // Vertical parallax — each image slides inside its overflow-hidden container
      // Matches dilacode's vertical-parallax class behavior
      const sectionImages = gridRef.current?.querySelectorAll('.section-image');
      sectionImages?.forEach((img) => {
        const parent = img.closest('.clapat-item');
        if (!parent) return;
        gsap.fromTo(
          img,
          { yPercent: -8 },
          {
            yPercent: 8,
            ease: 'none',
            scrollTrigger: {
              trigger: parent,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.2,
            },
          }
        );
      });
    }, gridRef);

    return () => ctx.revert();
  }, [loading]);

  if (loading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-6">
        <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-white/10 border-t-white" />
        <p className="text-sm text-white/50">{t.loading}</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-lg text-white/50">{t.empty}</p>
        <Link
          href={href('/contact')}
          className="inline-flex items-center justify-center rounded-full border border-white bg-transparent px-8 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-white hover:text-black"
        >
          {t.getInTouch}
        </Link>
      </div>
    );
  }

  return (
    <div ref={gridRef} className="showcase-portfolio">
      {projects.map((raw) => {
        // Catch-all for projects whose data.ts entry has no translation yet.
        const project = {
          ...raw,
          title: copy.fallbacks.titles[raw.title] ?? raw.title,
          category: copy.fallbacks.categories[raw.category] ?? raw.category,
        };
        return (
        <div key={project.id} className="showcase-item clapat-item">
          <div className="slide-inner trigger-item">
            <div className="img-mask">
              <Link
                href={`/datacode-portfolio/${project.slug}`}
                className="slide-link"
                aria-label={project.title}
                data-cursor-label={t.viewLabel}
              />
              <div className="section-image trigger-item-link">
                <Image
                  src={project.image}
                  alt={project.title}
                  fill
                  unoptimized
                  className="item-image"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw"
                />
              </div>
            </div>
            {/* Date badge — top left inside image */}
            <div
              className="showcase-slide-date"
              style={rtl ? { left: 'auto', insetInlineStart: 30 } : undefined}
            >
              <bdi dir="ltr">{project.year}</bdi>
            </div>
            {/* Caption below image */}
            <div
              className="slide-caption"
              style={rtl ? { textAlign: 'start' } : undefined}
            >
              <div className="slide-title">
                <span>{project.title}</span>
              </div>
              <div className="slide-cat">
                <span>{project.category}</span>
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
